import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, Instagram, Linkedin, MessageCircle, Mail, Globe, Monitor, Presentation } from 'lucide-react';
import { TypeAnimation } from 'react-type-animation';
import { useLanguage } from '../context/LanguageContext';
import { ImageModal, PayoutCarousel } from '../components/shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const GOLD = '#d6b36a';
const DISC = 'https://customer-assets.emergentagent.com/job_realestate-fund-6/artifacts/qsxu12yq_1_LE_upscale_balanced_x4.webp';
const MARK = 'https://customer-assets.emergentagent.com/job_accretion-axiom/artifacts/3rwpkqcm_7a40f4_da18f8518e4c4d5dbeedf37582cf828c~mv2.avif';

const LinksPage = () => {
  const { t, lang, setLang } = useLanguage();
  const navigate = useNavigate();
  const [data, setData] = useState({ profile: null, links: [] });
  const [failed, setFailed] = useState(false);
  const [modalImage, setModalImage] = useState({ isOpen: false, src: '', alt: '' });

  useEffect(() => {
    fetch(`${API_URL}/api/links/content`)
      .then(r => r.json())
      .then(setData)
      .catch(() => setFailed(true));
  }, []);

  const pick = (obj, field) => (lang === 'es' ? obj[`${field}_es`] || obj[field] : obj[field]) || '';

  const go = (url, id) => {
    if (id) fetch(`${API_URL}/api/links/items/${id}/click`, { method: 'POST' }).catch(() => {});
    const target = (url || '').trim();
    if (!target) return;
    if (target.startsWith('/')) navigate(target);
    else window.open(target.startsWith('http') || target.startsWith('mailto:') ? target : `https://${target}`, '_blank', 'noopener');
  };

  const profile = data.profile;
  if (!profile) return (
    <div className="min-h-screen flex items-center justify-center text-center px-6" style={{ backgroundColor: '#0b1017' }} data-testid="links-loading">
      <p className="text-gray-500 text-sm">
        {failed
          ? (lang === 'es' ? 'No se ha podido cargar. Recarga la página.' : 'Could not load. Please reload the page.')
          : (lang === 'es' ? 'Cargando...' : 'Loading...')}
      </p>
    </div>
  );

  const socials = [
    { key: 'instagram', url: profile.instagram, Icon: Instagram },
    { key: 'linkedin', url: profile.linkedin, Icon: Linkedin },
    { key: 'whatsapp', url: profile.whatsapp, Icon: MessageCircle },
    { key: 'email', url: profile.email ? `mailto:${profile.email}` : '', Icon: Mail },
  ].filter(s => s.url);

  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ backgroundColor: '#0b1017' }} data-testid="links-page">
      <style>{`
        @keyframes linkRise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes discSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .rise { opacity: 0; animation: linkRise .7s cubic-bezier(.22,.61,.36,1) forwards; }
      `}</style>

      {/* Brand disc + ambient glow */}
      <div className="pointer-events-none absolute -top-72 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full overflow-hidden"
        style={{ maskImage: 'radial-gradient(circle, #000 42%, transparent 68%)', WebkitMaskImage: 'radial-gradient(circle, #000 42%, transparent 68%)' }}>
        <img src={DISC} alt="" aria-hidden
          className="w-full h-full object-cover opacity-[0.18]"
          style={{ mixBlendMode: 'screen', animation: 'discSpin 160s linear infinite' }} />
      </div>
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[620px] h-[620px] rounded-full opacity-[0.14]"
        style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 65%)` }} />

      {/* Language toggle */}
      <div className="absolute top-5 right-5 z-20 flex items-center gap-1 border border-gray-800 rounded-full px-1 py-1 bg-black/40 backdrop-blur">
        <Globe size={13} className="text-gray-500 ml-2 mr-1" />
        {['en', 'es'].map(code => (
          <button key={code} onClick={() => setLang(code)}
            className={`text-xs px-2.5 py-1 rounded-full transition-colors ${lang === code ? 'bg-white text-black font-semibold' : 'text-gray-400 hover:text-white'}`}
            data-testid={`links-lang-${code}`}>
            {code.toUpperCase()}
          </button>
        ))}
      </div>

      <div className="relative z-10 max-w-3xl mx-auto px-5 pt-14 pb-20 md:pt-20">
        {/* Header */}
        <header className="rise text-center" style={{ animationDelay: '60ms' }}>
          <div className="inline-flex items-center justify-center rounded-full p-[3px] mx-auto"
            style={{ background: `conic-gradient(from 180deg, ${GOLD}, #ffffff22, ${GOLD})` }}>
            <img src={profile.avatar || MARK} alt={profile.name}
              className="h-28 w-28 md:h-32 md:w-32 rounded-full object-cover bg-[#0b1017]"
              data-testid="links-avatar" />
          </div>
          <h1 className="text-4xl md:text-6xl font-semibold mt-6 leading-tight" style={SERIF} data-testid="links-name">{profile.name}</h1>
          <p className="text-[11px] md:text-sm uppercase tracking-[0.3em] mt-3" style={{ color: GOLD }} data-testid="links-headline">{pick(profile, 'headline')}</p>

          <p className="text-sm md:text-base font-bold tracking-[0.1em] mt-6 text-gray-200 leading-relaxed" data-testid="links-tagline">
            {t('home.heroLine1')} {t('home.heroLine2')}{' '}
            <TypeAnimation
              key={lang}
              sequence={[t('home.heroAnim1'), 6000, t('home.heroAnim2'), 6000, t('home.heroAnim3'), 6000]}
              wrapper="span"
              speed={10}
              className="text-white"
              repeat={Infinity}
            />
          </p>

          {socials.length > 0 && (
            <div className="flex justify-center gap-3 mt-7" data-testid="links-socials">
              {socials.map(({ key, url, Icon }) => (
                <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                  className="h-14 w-14 rounded-full border border-gray-700 bg-white/[0.04] flex items-center justify-center text-gray-300 hover:text-black hover:bg-white hover:border-white hover:-translate-y-1 transition-all duration-300"
                  data-testid={`links-social-${key}`}>
                  <Icon size={22} />
                </a>
              ))}
            </div>
          )}
        </header>

        {/* Offers = one card per business, each with WEB + PRESENTATION */}
        {profile.offers?.length > 0 && (
          <section className="mt-16 rise text-center" style={{ animationDelay: '200ms' }} data-testid="links-offers">
            <h2 className="text-2xl md:text-3xl font-semibold mb-6" style={SERIF}>{pick(profile, 'offers_title')}</h2>
            <div className="space-y-4">
              {profile.offers.map((offer, i) => (
                <article key={i} className="border border-gray-800 bg-white/[0.03] backdrop-blur-sm rounded-2xl p-5 md:p-6 hover:border-gray-600 transition-colors"
                  data-testid={`links-offer-${i}`}>
                  {pick(offer, 'metric') && (
                    <span className="inline-block text-[10px] md:text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full border mb-3"
                      style={{ color: GOLD, borderColor: 'rgba(214,179,106,.4)' }}>{pick(offer, 'metric')}</span>
                  )}
                  <h3 className="font-semibold text-lg md:text-2xl leading-snug mb-2" style={SERIF}>{pick(offer, 'title')}</h3>
                  <p className="text-gray-400 text-[13px] md:text-sm leading-relaxed max-w-xl mx-auto">{pick(offer, 'desc')}</p>
                  {(offer.web_url || offer.deck_url) && (
                    <div className="flex flex-wrap justify-center gap-2.5 mt-5">
                      {offer.web_url && (
                        <button onClick={() => go(offer.web_url)}
                          className="group inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase px-4 py-2.5 rounded-full bg-white text-black hover:bg-gray-200 transition-colors"
                          data-testid={`offer-web-${i}`}>
                          <Monitor size={14} /> {lang === 'es' ? 'Web' : 'Web'}
                          <ArrowUpRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                        </button>
                      )}
                      {offer.deck_url && (
                        <button onClick={() => go(offer.deck_url)}
                          className="group inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase px-4 py-2.5 rounded-full border transition-colors hover:bg-white/5"
                          style={{ color: GOLD, borderColor: 'rgba(214,179,106,.45)' }}
                          data-testid={`offer-deck-${i}`}>
                          <Presentation size={14} /> {lang === 'es' ? 'Presentación' : 'Presentation'}
                          <ArrowUpRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                        </button>
                      )}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Verified prop firm payouts */}
        <section className="mt-16 rise text-center" style={{ animationDelay: '320ms' }} data-testid="links-payouts">
          <h2 className="text-2xl md:text-3xl font-semibold mb-2" style={SERIF}>
            {lang === 'es' ? 'Resultados verificados' : 'Verified results'}
          </h2>
          <p className="text-gray-500 text-xs md:text-sm mb-6">
            {lang === 'es' ? 'Pagos reales de prop firms y certificados que puedes comprobar tú mismo.' : 'Real prop firm payouts and certificates you can verify yourself.'}
          </p>
          <div className="-mx-5 md:mx-0">
            <PayoutCarousel openModal={(src, alt) => setModalImage({ isOpen: true, src, alt })} />
          </div>
        </section>

        {/* Extra links */}
        {data.links.length > 0 && (
          <section className="mt-16 space-y-3 text-center" data-testid="links-list">
            {data.links.map((item, i) => (
              <button key={item.id} onClick={() => go(item.url, item.id)}
                className={`group w-full rounded-xl px-5 py-4 flex items-center gap-4 transition-all hover:-translate-y-0.5 rise ${
                  item.featured ? 'border bg-gradient-to-r from-white/[0.08] to-transparent' : 'border border-gray-800 bg-white/[0.02] hover:border-gray-600'
                }`}
                style={{ animationDelay: `${420 + i * 70}ms`, borderColor: item.featured ? GOLD : undefined }}
                data-testid={`link-item-${i}`}>
                {item.image && <img src={item.image} alt="" className="h-12 w-12 rounded-lg object-cover flex-shrink-0 ring-1 ring-white/10" />}
                <span className="flex-1 min-w-0">
                  <span className="flex items-center justify-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm md:text-base">{pick(item, 'title')}</span>
                    {pick(item, 'badge') && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border" style={{ color: GOLD, borderColor: 'rgba(214,179,106,.4)' }}>
                        {pick(item, 'badge')}
                      </span>
                    )}
                  </span>
                  {pick(item, 'subtitle') && <span className="block text-gray-500 text-xs md:text-sm mt-1 leading-snug">{pick(item, 'subtitle')}</span>}
                </span>
                <ArrowUpRight size={18} className="text-gray-600 group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all flex-shrink-0" />
              </button>
            ))}
          </section>
        )}

        <footer className="mt-16 text-center rise" style={{ animationDelay: '700ms' }}>
          <a href="/" className="text-gray-600 text-xs tracking-[0.2em] uppercase hover:text-gray-300 transition-colors" data-testid="links-home">
            accretionaxiomcapital.com
          </a>
        </footer>
      </div>

      <ImageModal isOpen={modalImage.isOpen} onClose={() => setModalImage({ isOpen: false, src: '', alt: '' })} imageSrc={modalImage.src} imageAlt={modalImage.alt} />
    </div>
  );
};

export default LinksPage;
